<script lang="ts">
	import { Heading, Radio } from "flowbite-svelte-v1";
	import { getContext, onMount } from "svelte";
	import { type Writable } from "svelte/store";
	import type { BrandKit } from "#gen/brands/v1/service_pb.js";
	import type { Poll } from "#gen/forms/v1/poll_pb.js";
	import type { Page } from "#gen/pages/v2/page_pb.js";
	import Label from "#lib/components/Label.svelte";
	import ColorInputV2 from "#lib/components/flow/editor/appearance/ColorInputV2.svelte";
	import { PollState } from "#lib/components/page/components/poll/stateMachine.js";
	import { getDefaults, getTheme, getThemes, setThemeValue } from "#lib/components/page/components/poll/theme.js";
	import { setEditorState } from "#lib/components/page/components/poll/utils.js";
	import { setStructValue, structValue } from "#lib/struct.js";
	import type { JsonObject } from "@bufbuild/protobuf";

	interface Props {
		id: string;
		page: Writable<Page>;
		poll: Poll;
		onChange?: (poll: Poll) => void;
		onInput?: (poll: Poll) => void;
	}

	let { id, page, poll, onChange, onInput }: Props = $props();

	const editorState: Writable<Record<string, unknown>> = getContext("editorState");
	const brandKit: Writable<BrandKit | undefined> = getContext("brandKit");

	const themeOptions = $derived(getThemes($brandKit));
	const config = $derived(getConfig($page, id));
	const themeDefaults = $derived(getDefaults($brandKit, $page));
	const theme = $derived(getTheme($brandKit, $page, config));

	// svelte-ignore state_referenced_locally
	let themeSelection = $state(isCustomTheme(config) ? "custom" : "base");

	onMount(() => {
		$editorState = setEditorState($editorState, poll, "overridePollState", PollState.Active);
	});

	function getConfig(page: Page, id: string): JsonObject {
		let config = structValue(page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue(page.config, id, config);
		}
		return config;
	}

	function resetColorValues(_event: Event) {
		setStructValue($page.config, id, {});
		handleValueChange();
	}

	function setValue(key: string, value: string | undefined) {
		setThemeValue($brandKit, $page, config, key, value);
		themeSelection = isCustomTheme(config) ? "custom" : "base";
		onInput?.(poll);
	}

	function isCustomTheme(config: JsonObject): boolean {
		return Object.keys(config).some(key => config[key] !== themeDefaults[key]);
	}

	function handleValueChange() {
		onChange?.(poll);
	}
</script>

<div class="flex flex-col gap-6">
	<div class="grid w-full grid-cols-3 gap-4">
		{#each themeOptions as themeOption (themeOption.value)}
			<Radio name="theme" custom value={themeOption.value} onchange={resetColorValues} bind:group={themeSelection}>
				<div class="peer-checked:border-primary-600 flex w-full cursor-pointer flex-col items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-14 py-6 text-gray-500 hover:bg-gray-100 hover:text-gray-600">
					<div class="h-15 w-14 overflow-clip rounded-lg shadow-md">
						<div class="h-1/2" style="background-color: {themeOption.topColor};"></div>
						<div class="grid h-1/2 w-full grid-cols-2">
							<div class="h-full" style="background-color: {themeOption.bottomColor};"></div>
							<div class="h-full w-full bg-white"></div>
						</div>
					</div>
					<div class="text-center text-sm font-medium">{themeOption.label}</div>
				</div>
			</Radio>
		{/each}
	</div>
	<Heading tag="h4" class="border-b pb-1 text-sm font-semibold">UI elements</Heading>
	<div class="flex flex-col gap-4">
		<div class="grid w-full grid-cols-[1fr_2fr] gap-4">
			<Label for="paragraphTextColor" class="mt-2.5 grow">Primary text</Label>
			<ColorInputV2 id="paragraphTextColor" class="w-full" resetColor={themeDefaults["primaryTextColor"] as string} display="inline" value={theme["primaryTextColor"] as string} oninput={(value: string) => setValue("primaryTextColor", value)} onchange={handleValueChange} />
			<Label for="headerTextColor" class="mt-2.5 grow">Secondary text</Label>
			<ColorInputV2 id="headerTextColor" class="w-full" resetColor={themeDefaults["secondaryTextColor"] as string} display="inline" value={theme["secondaryTextColor"] as string} oninput={(value: string) => setValue("secondaryTextColor", value)} onchange={handleValueChange} />
			<Label for="primaryButtonBackgroundColor" class="mt-2.5 grow">Primary button background</Label>
			<ColorInputV2 id="primaryButtonBackgroundColor" allowTransparent class="w-full" resetColor={themeDefaults["primaryButtonBackgroundColor"] as string} display="inline" value={theme["primaryButtonBackgroundColor"] as string} oninput={(value: string) => setValue("primaryButtonBackgroundColor", value)} onchange={handleValueChange} />
			<Label for="primaryButtonTextColor" class="mt-2.5 grow">Primary button text</Label>
			<ColorInputV2 id="primaryButtonTextColor" class="w-full" resetColor={themeDefaults["primaryButtonTextColor"] as string} display="inline" value={theme["primaryButtonTextColor"] as string} oninput={(value: string) => setValue("primaryButtonTextColor", value)} onchange={handleValueChange} />
			<Label for="secondaryButtonBackgroundColor" class="mt-2.5 grow">Secondary button background</Label>
			<ColorInputV2 id="secondaryButtonBackgroundColor" allowTransparent class="w-full" resetColor={themeDefaults["secondaryButtonBackgroundColor"] as string} display="inline" value={theme["secondaryButtonBackgroundColor"] as string} oninput={(value: string) => setValue("secondaryButtonBackgroundColor", value)} onchange={handleValueChange} />
			<Label for="secondaryButtonTextColor" class="mt-2.5 grow">Secondary button text</Label>
			<ColorInputV2 id="secondaryButtonTextColor" class="w-full" resetColor={themeDefaults["secondaryButtonTextColor"] as string} display="inline" value={theme["secondaryButtonTextColor"] as string} oninput={(value: string) => setValue("secondaryButtonTextColor", value)} onchange={handleValueChange} />
		</div>
	</div>
	<Heading tag="h4" class="border-b pb-1 text-sm font-semibold">Module appearance</Heading>
	<div class="flex flex-col gap-4">
		<div class="grid w-full grid-cols-[1fr_2fr] gap-4">
			<Label for="backgroundColor" class="mt-2.5 grow">Background</Label>
			<ColorInputV2 id="backgroundColor" allowTransparent class="w-full" resetColor={themeDefaults["backgroundColor"] as string} display="inline" value={theme["backgroundColor"] as string} oninput={(value: string) => setValue("backgroundColor", value)} onchange={handleValueChange} />
			<Label for="borderColor" class="mt-2.5 grow">Border</Label>
			<ColorInputV2 id="borderColor" class="w-full" resetColor={themeDefaults["borderColor"] as string} display="inline" value={theme["borderColor"] as string} oninput={(value: string) => setValue("borderColor", value)} onchange={handleValueChange} />
			<Label for="navigationColor" class="mt-2.5 grow">Navigation</Label>
			<ColorInputV2 id="navigationColor" class="w-full" resetColor={themeDefaults["navigationColor"] as string} display="inline" value={theme["navigationColor"] as string} oninput={(value: string) => setValue("navigationColor", value)} onchange={handleValueChange} />
		</div>
	</div>
</div>
